import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../App";
import styles from "./ProvaHistory.module.css";

// Types
interface ProvaHistoryType {
    id: number;
    teamId: number;
    teamName: string;
    provaType: "segueLinha" | "velocidade";
    startTime: number;
    endTime: number;
    disqualified: boolean;
    penalties: PenaltyType[];
}

interface PenaltyType {
    id: number;
    provaId: number;
    nome: string;
    value: number;
}

const ProvaHistory: React.FC = () => {
    const { isDarkMode } = useContext(ThemeContext);
    const [provaHistory, setProvaHistory] = useState<ProvaHistoryType[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<"all" | "segueLinha" | "velocidade">(
        "all"
    );

    // Calculate final time including penalties
    const calculateFinalTime = (
        baseTime: number,
        penalties: PenaltyType[]
    ): number => {
        const penaltyTime = penalties.reduce(
            (total, penalty) => total + penalty.value,
            0
        );
        return baseTime + penaltyTime;
    };

    // Format date and time
    const formatDateTime = (timestamp: number) => {
        return new Date(timestamp).toLocaleString();
    };

    // Fetch prova history
    const fetchProvaHistory = async () => {
        try {
            setLoading(true);
            const response = await axios.get("/provas/history");
            setProvaHistory(response.data);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching prova history:", error);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProvaHistory();
    }, []);

    // Get filtered history based on selected filter
    const getFilteredHistory = () => {
        if (filter === "all") {
            return provaHistory;
        }
        return provaHistory.filter((prova) => prova.provaType === filter);
    };

    // Group results by prova type for leaderboard views
    const getLeaderboardsByType = () => {
        const segueLinha = provaHistory
            .filter((p) => p.provaType === "segueLinha" && !p.disqualified)
            .map((p) => ({
                ...p,
                finalTime: calculateFinalTime(
                    p.endTime - p.startTime,
                    p.penalties
                ),
            }))
            .sort((a, b) => a.finalTime - b.finalTime);

        const velocidade = provaHistory
            .filter((p) => p.provaType === "velocidade" && !p.disqualified)
            .map((p) => ({
                ...p,
                finalTime: calculateFinalTime(
                    p.endTime - p.startTime,
                    p.penalties
                ),
            }))
            .sort((a, b) => a.finalTime - b.finalTime);

        return { segueLinha, velocidade };
    };

    const leaderboards = getLeaderboardsByType();

    if (loading) {
        return <div className={styles.loading}>Carregando dados...</div>;
    }

    return (
        <div
            className={`${styles.provaHistoryContainer} ${
                isDarkMode ? styles.darkTheme : styles.lightTheme
            }`}>
            <h1>Histórico de Provas</h1>

            <div className={styles.filterBar}>
                <button
                    className={`${styles.filterButton} ${
                        filter === "all" ? styles.active : ""
                    }`}
                    onClick={() => setFilter("all")}>
                    Todas
                </button>
                <button
                    className={`${styles.filterButton} ${
                        filter === "segueLinha" ? styles.active : ""
                    }`}
                    onClick={() => setFilter("segueLinha")}>
                    Segue Linha
                </button>
                <button
                    className={`${styles.filterButton} ${
                        filter === "velocidade" ? styles.active : ""
                    }`}
                    onClick={() => setFilter("velocidade")}>
                    Velocidade
                </button>
                <button
                    className={styles.refreshButton}
                    onClick={fetchProvaHistory}>
                    🔄 Atualizar
                </button>
            </div>

            {/* Summary Cards */}
            <div className={styles.summaryCards}>
                <div className={styles.summaryCard}>
                    <h3>Segue Linha</h3>
                    <div className={styles.statValue}>
                        {
                            provaHistory.filter(
                                (p) => p.provaType === "segueLinha"
                            ).length
                        }
                    </div>
                    <div className={styles.statLabel}>Provas realizadas</div>
                    {leaderboards.segueLinha.length > 0 && (
                        <div className={styles.topTeam}>
                            <span className={styles.badge}>1º</span>
                            <span className={styles.teamName}>
                                {leaderboards.segueLinha[0].teamName}
                            </span>
                            <span className={styles.teamTime}>
                                {(
                                    leaderboards.segueLinha[0].finalTime / 1000
                                ).toFixed(2)}
                                s
                            </span>
                        </div>
                    )}
                </div>

                <div className={styles.summaryCard}>
                    <h3>Velocidade</h3>
                    <div className={styles.statValue}>
                        {
                            provaHistory.filter(
                                (p) => p.provaType === "velocidade"
                            ).length
                        }
                    </div>
                    <div className={styles.statLabel}>Provas realizadas</div>
                    {leaderboards.velocidade.length > 0 && (
                        <div className={styles.topTeam}>
                            <span className={styles.badge}>1º</span>
                            <span className={styles.teamName}>
                                {leaderboards.velocidade[0].teamName}
                            </span>
                            <span className={styles.teamTime}>
                                {(
                                    leaderboards.velocidade[0].finalTime / 1000
                                ).toFixed(2)}
                                s
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Detailed History */}
            <div className={styles.historyList}>
                <h2>Detalhes das Provas</h2>

                {getFilteredHistory().length > 0 ? (
                    getFilteredHistory().map((prova) => (
                        <div key={prova.id} className={styles.historyItem}>
                            <div className={styles.historyHeader}>
                                <div className={styles.headerLeft}>
                                    <span
                                        className={`${styles.provaTypeBadge} ${
                                            prova.provaType === "segueLinha"
                                                ? styles.segueLinha
                                                : styles.velocidade
                                        }`}>
                                        {prova.provaType === "segueLinha"
                                            ? "Segue Linha"
                                            : "Velocidade"}
                                    </span>
                                    <h3>{prova.teamName}</h3>
                                </div>
                                {prova.disqualified && (
                                    <span className={styles.disqualifiedBadge}>
                                        Desqualificado
                                    </span>
                                )}
                            </div>

                            <div className={styles.historyDetails}>
                                <div className={styles.detailRow}>
                                    <span className={styles.detailLabel}>
                                        Início:
                                    </span>
                                    <span className={styles.detailValue}>
                                        {formatDateTime(prova.startTime)}
                                    </span>
                                </div>
                                <div className={styles.detailRow}>
                                    <span className={styles.detailLabel}>
                                        Fim:
                                    </span>
                                    <span className={styles.detailValue}>
                                        {formatDateTime(prova.endTime)}
                                    </span>
                                </div>
                                <div className={styles.detailRow}>
                                    <span className={styles.detailLabel}>
                                        Tempo Base:
                                    </span>
                                    <span className={styles.detailValue}>
                                        {(
                                            (prova.endTime - prova.startTime) /
                                            1000
                                        ).toFixed(2)}
                                        s
                                    </span>
                                </div>

                                {!prova.disqualified && (
                                    <div className={styles.detailRow}>
                                        <span className={styles.detailLabel}>
                                            Tempo Final:
                                        </span>
                                        <span
                                            className={`${styles.detailValue} ${styles.finalTime}`}>
                                            {(
                                                calculateFinalTime(
                                                    prova.endTime -
                                                        prova.startTime,
                                                    prova.penalties
                                                ) / 1000
                                            ).toFixed(2)}
                                            s
                                        </span>
                                    </div>
                                )}
                            </div>

                            {prova.penalties.length > 0 && (
                                <div className={styles.penaltiesSection}>
                                    <h4>Penalidades</h4>
                                    <ul className={styles.penaltiesList}>
                                        {prova.penalties.map((penalty) => (
                                            <li
                                                key={penalty.id}
                                                className={styles.penaltyItem}>
                                                <span
                                                    className={
                                                        styles.penaltyName
                                                    }>
                                                    {penalty.nome}
                                                </span>
                                                <span
                                                    className={
                                                        styles.penaltyValue
                                                    }>
                                                    +{penalty.value}s
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    ))
                ) : (
                    <div className={styles.emptyState}>
                        Nenhuma prova encontrada para os filtros selecionados.
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProvaHistory;
